import { Component, inject, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Event } from './events.service';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ReactiveFormsModule } from '@angular/forms';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { CommonModule } from '@angular/common';
import { ViewChild, ElementRef } from '@angular/core';
import { CloudinaryUploadService } from '../upload-ui/cloudinary-upload.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { AuthService } from '../shared/auth/auth.service';
import { authBackendEndPointToken, backendEndPointToken } from '../app.config';
import { HttpClient } from '@angular/common/http';
import { MatChipsModule } from '@angular/material/chips';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MatDialog } from '@angular/material/dialog';
import { ImageCropDialogComponent } from './image-crop-dialog.component';
import { AVAILABLE_PRACTITIONER_ROLE_OPTIONS } from '../shared/system-roles';
interface FormDialogData {
  mode: 'create' | 'edit';
  event?: Event;
  organizationId?: string;
  organizations?: { id: string; name: string }[];
}

@Component({
  selector: 'app-event-form-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatProgressSpinnerModule,
    MatCheckboxModule,
    MatChipsModule,
  ],
  template: `
    <div class="event-form-dialog">
      <div class="dialog-header">
        <div class="header-content">
          <mat-icon class="header-icon">{{ data.mode === 'create' ? 'add_circle' : 'edit' }}</mat-icon>
          <h2 class="header-title">{{ data.mode === 'create' ? 'Create New Event' : 'Edit Event' }}</h2>
        </div>
        <button mat-icon-button (click)="close()" class="close-btn">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <mat-dialog-content>
        <form [formGroup]="eventForm" class="event-form">
          <!-- Timezone Info Banner -->


          <!-- Title -->
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Event Title</mat-label>
            <input matInput formControlName="title" placeholder="Enter event title">
            <mat-error *ngIf="eventForm.get('title')?.hasError('required')">
              Title is required
            </mat-error>
          </mat-form-field>

          <!-- Description -->
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Description</mat-label>
            <textarea
              matInput
              formControlName="description"
              placeholder="Enter event description"
              rows="4">
            </textarea>
          </mat-form-field>

          <!-- Date and Time Row -->
          <div class="form-row">
            <div class="date-time-group">
              <mat-form-field appearance="outline" class="date-field">
                <mat-label>Start Date</mat-label>
                <input
  matInput
  [matDatepicker]="startPicker"
  formControlName="startDate"
  [min]="minDate"
  placeholder="Select start date">
                <mat-datepicker-toggle matIconSuffix [for]="startPicker"></mat-datepicker-toggle>
                <mat-datepicker #startPicker></mat-datepicker>
                <mat-error *ngIf="eventForm.get('startDate')?.hasError('required')">
                  Start date is required
                </mat-error>
              </mat-form-field>

              <mat-form-field appearance="outline" class="time-field">
                <mat-label>Start Time</mat-label>
                <mat-select formControlName="startTime">
                  <mat-option *ngFor="let time of timeOptions" [value]="time">
                    {{ time }}
                  </mat-option>
                </mat-select>
                <mat-icon matIconSuffix>schedule</mat-icon>
                <mat-error *ngIf="eventForm.get('startTime')?.hasError('required')">
                  Start time is required
                </mat-error>
              </mat-form-field>
            </div>

            <div class="date-time-group">
              <mat-form-field appearance="outline" class="date-field">
                <mat-label>End Date</mat-label>
                <input
                    matInput
                    [matDatepicker]="endPicker"
                    formControlName="endDate"
                    [min]="minDate"
                    placeholder="Select end date">
                <mat-datepicker-toggle matIconSuffix [for]="endPicker"></mat-datepicker-toggle>
                <mat-datepicker #endPicker></mat-datepicker>
                <mat-error *ngIf="eventForm.get('endDate')?.hasError('required')">
                  End date is required
                </mat-error>
              </mat-form-field>

              <mat-form-field appearance="outline" class="time-field">
                <mat-label>End Time</mat-label>
                <mat-select formControlName="endTime">
                  <mat-option *ngFor="let time of timeOptions" [value]="time">
                    {{ time }}
                  </mat-option>
                </mat-select>
                <mat-icon matIconSuffix>schedule</mat-icon>
                <mat-error *ngIf="eventForm.get('endTime')?.hasError('required')">
                  End time is required
                </mat-error>
              </mat-form-field>
            </div>
            <div class="form-error-banner" *ngIf="dateRangeError">
  <mat-icon>error_outline</mat-icon>
  <span>End date/time must be after the start date/time.</span>
</div>
<div class="form-error-banner" *ngIf="pastDateError">
  <mat-icon>error_outline</mat-icon>
  <span>Event start time cannot be in the past.</span>
</div>
<div class="form-error-banner" *ngIf="durationError">
  <mat-icon>error_outline</mat-icon>
  <span>{{ autoMeetingProvider }} meetings can't exceed {{ providerMaxDurationHours[autoMeetingProvider!] }} hours. For multi-day events, enable "Recurring event" instead.</span>
</div>
          </div>

          <!-- Type and Platform Row -->
          <div class="form-row">
            <mat-form-field appearance="outline">
              <mat-label>Event Type</mat-label>
              <mat-select formControlName="type">
                <mat-option value="training">Training</mat-option>
                <mat-option value="webinar">Webinar</mat-option>
                <mat-option value="workshop">Workshop</mat-option>
                <mat-option value="conference">Conference</mat-option>
                <mat-option value="seminar">Seminar</mat-option>
                <mat-option value="meetup">Meetup</mat-option>
                <mat-option value="networking">Networking Event</mat-option>
              </mat-select>
              <mat-error *ngIf="eventForm.get('type')?.hasError('required')">
                Event type is required
              </mat-error>
            </mat-form-field>

           <mat-form-field appearance="outline">
  <mat-label>Platform</mat-label>
  <mat-select formControlName="platform">
    <mat-option value="Physical">Physical</mat-option>
    <mat-option value="Hybrid">Hybrid</mat-option>
    <mat-option value="Zoom">Zoom</mat-option>
    <mat-option value="Google Meet">Google Meet</mat-option>
    <mat-option value="Microsoft Teams">Microsoft Teams</mat-option>
    <mat-option value="Webex">Webex</mat-option>
  </mat-select>
  <mat-error *ngIf="eventForm.get('platform')?.hasError('required')">
    Platform is required
  </mat-error>
</mat-form-field>

<mat-form-field appearance="outline" class="full-width" *ngIf="isHybridPlatform">
  <mat-label>Virtual Platform</mat-label>
  <mat-select formControlName="virtualPlatformType">
    <mat-option value="Zoom">Zoom</mat-option>
    <mat-option value="Google Meet">Google Meet</mat-option>
    <mat-option value="Microsoft Teams">Microsoft Teams</mat-option>
    <mat-option value="Webex">Webex</mat-option>
  </mat-select>
  <mat-hint>Tool you'll use for remote attendees</mat-hint>
  <mat-error *ngIf="eventForm.get('virtualPlatformType')?.hasError('required')">
    Please select a virtual platform for the online portion
  </mat-error>
</mat-form-field>

<mat-checkbox formControlName="isRecurring" *ngIf="isAutoGeneratedMeeting">
  This is a recurring event (same link, multiple sessions)
</mat-checkbox>

<mat-form-field appearance="outline" class="full-width" *ngIf="eventForm.get('isRecurring')?.value">
  <mat-label>Repeats</mat-label>
  <mat-select formControlName="recurrenceFrequency">
    <mat-option value="DAILY">Daily</mat-option>
    <mat-option value="WEEKLY">Weekly</mat-option>
    <mat-option value="MONTHLY">Monthly</mat-option>
    <mat-option value="YEARLY">Annually</mat-option>
  </mat-select>
</mat-form-field>

<mat-form-field appearance="outline" class="full-width" *ngIf="eventForm.get('isRecurring')?.value">
  <mat-label>Number of sessions</mat-label>
  <input matInput type="number" formControlName="recurrenceCount" min="2" max="365">
</mat-form-field>
          </div>

          <!-- Location -->
      <mat-form-field appearance="outline" class="full-width" *ngIf="showLocationField">
      <mat-label>Location</mat-label>
      <input matInput formControlName="location" placeholder="Enter event location">
      <mat-error *ngIf="eventForm.get('location')?.hasError('required')">
        Location is required
      </mat-error>
    </mat-form-field>
<mat-form-field appearance="outline" class="full-width" *ngIf="isSuperAdmin">
  <mat-label>Target Organizations</mat-label>
  <mat-select formControlName="targetOrganizationIds" multiple (selectionChange)="onTargetOrgsChange($event)">
    <mat-option value="all">All Organizations</mat-option>
    <mat-option *ngFor="let org of organizations" [value]="org.id">{{ org.name }}</mat-option>
  </mat-select>
  <mat-hint>Choose "All Organizations", or select one or more specific organizations.</mat-hint>
</mat-form-field>
          <!-- Participants (Roles) -->
        <mat-form-field appearance="outline" class="full-width">
  <mat-label>Target Participants</mat-label>
  <mat-select formControlName="participants" multiple>
    <mat-option *ngFor="let option of participantOptions" [value]="option.code">
      {{ option.display }}
    </mat-option>
  </mat-select>
  <mat-hint>Select "Public" for open events, or specific roles — you can combine both</mat-hint>
</mat-form-field>

          <!-- Max Attendees and Type Row -->
          <div class="form-row">
            <mat-form-field appearance="outline">
              <mat-label>Max Attendees</mat-label>
              <input matInput type="number" formControlName="maxAttendees" placeholder="Leave empty for unlimited">
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Tags</mat-label>
              <input matInput formControlName="tags" placeholder="Comma-separated tags">
              <mat-hint>e.g., healthcare, training, certification</mat-hint>
            </mat-form-field>
          </div>

<!-- Meeting URL -->
<mat-form-field appearance="outline" class="full-width">
  <mat-label>{{ isAutoGeneratedMeeting ? (autoMeetingProvider + ' Meeting (auto-generated)') : 'Meeting/Join URL' }}</mat-label>
  <input matInput formControlName="meetingUrl"
         [placeholder]="isAutoGeneratedMeeting ? 'Will be created automatically when you save' : 'https://...'">
  <mat-icon matSuffix *ngIf="isAutoGeneratedMeeting">bolt</mat-icon>
  <mat-hint *ngIf="isAutoGeneratedMeeting">
    {{ autoMeetingProvider }} will create this meeting automatically.
  </mat-hint>
  <mat-error *ngIf="eventForm.get('meetingUrl')?.hasError('pattern')">
    Please enter a valid URL
  </mat-error>
</mat-form-field>

<!-- Registration URL — independent of platform -->
<mat-form-field appearance="outline" class="full-width">
  <mat-label>Registration Link (optional)</mat-label>
  <input matInput formControlName="registrationUrl"
         placeholder="https://forms.google.com/... or external RSVP page">
  <mat-icon matSuffix>how_to_reg</mat-icon>
  <mat-hint>
    Use this if attendees should sign up separately before receiving the join link
  </mat-hint>
  <mat-error *ngIf="eventForm.get('registrationUrl')?.hasError('pattern')">
    Please enter a valid URL
  </mat-error>
</mat-form-field>
<!-- External Attendees -->
<div class="external-attendees-field">
  <label class="field-label">
    <mat-icon class="field-label-icon">alternate_email</mat-icon>
    Cohost (Optional)
  </label>

  <div class="chip-input-container" [class.has-error]="externalEmailError">
    <mat-icon class="chip-input-icon">mail_outline</mat-icon>

    <mat-chip-grid #chipGrid aria-label="External attendee emails" class="chip-grid">
      <mat-chip-row *ngFor="let email of externalAttendeeEmails" (removed)="removeExternalEmail(email)">
        {{ email }}
        <button matChipRemove aria-label="Remove email">
          <mat-icon>cancel</mat-icon>
        </button>
      </mat-chip-row>

      <input
        matInput
        class="external-attendee-input"
        placeholder="e.g. jane@example.com — press Enter or comma to add"
        [matChipInputFor]="chipGrid"
        [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
        (matChipInputTokenEnd)="addExternalEmail($event)">
    </mat-chip-grid>
  </div>

  <div class="upload-error" *ngIf="externalEmailError">
    <mat-icon class="error-icon">error_outline</mat-icon>
    {{ externalEmailError }}
  </div>

  <span class="field-hint" *ngIf="!externalEmailError">
    People outside the platform who should be invited by email — they'll get a calendar invite and event details.
  </span>
</div>
          <!-- Image URL -->
         <div class="image-upload-field">
  <label class="image-upload-label">Event Image</label>

  <input
    #imageFileInput
    type="file"
    accept="image/*"
    hidden
    (change)="onImageFileSelected($event)">

  <!-- No image yet: show upload button -->
  <div class="image-upload-box" *ngIf="!eventForm.get('image')?.value">
    <button mat-stroked-button type="button" (click)="triggerImageUpload()" [disabled]="isUploadingImage">
      <mat-icon>upload</mat-icon>
      {{ isUploadingImage ? 'Uploading...' : 'Upload Image' }}
    </button>
    <mat-spinner *ngIf="isUploadingImage" diameter="20"></mat-spinner>
  </div>

  <!-- Image already set: show preview + replace/remove -->
  <div class="image-preview" *ngIf="eventForm.get('image')?.value">
    <img [src]="eventForm.get('image')?.value" alt="Event image preview">
    <button mat-icon-button type="button" class="remove-image-btn"
            (click)="removeImage()" [disabled]="isUploadingImage">
      <mat-icon>close</mat-icon>
    </button>
    <button mat-stroked-button type="button" (click)="triggerImageUpload()" [disabled]="isUploadingImage">
      {{ isUploadingImage ? 'Uploading...' : 'Replace' }}
    </button>
  </div>

  <div class="upload-error" *ngIf="imageUploadError">{{ imageUploadError }}</div>
</div>
        </form>
      </mat-dialog-content>

      <mat-dialog-actions align="end">
        <button mat-button (click)="close()">Cancel</button>
        <button
          mat-raised-button
          color="primary"
          (click)="onSubmit()"
          [disabled]="eventForm.invalid">
          {{ data.mode === 'create' ? 'Create Event' : 'Update Event' }}
        </button>
      </mat-dialog-actions>
    </div>
  `,
  styles: [`
    .event-form-dialog {
      display: flex;
      flex-direction: column;
      width: 100%;
      min-width: 0;
    }
.image-upload-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.external-attendees-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.field-label {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.7);
  font-weight: 600;
}
.external-attendee-input {
  border: none;
  outline: none;
  padding: 0.5rem 0;
  width: 100%;
  font: inherit;
}
.field-hint {
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.5);
}
mat-chip-grid {
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  padding: 0.5rem;
  min-height: 44px;
}
.image-preview {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;

  img {
    max-width: 200px;
    max-height: 150px;
    border-radius: 0.5rem;
    object-fit: cover;
    display: block;
  }
  .remove-image-btn {
    position: absolute;
    top: -10px;
    right: -10px;
    background: white;
    box-shadow: 0 1px 4px rgba(0,0,0,.2);
  }
}
.upload-error {
  color: #dc2626;
  font-size: 0.8rem;
}
.image-upload-label {
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.6);
  font-weight: 500;
}
.image-upload-box {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

    .dialog-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      background: linear-gradient(135deg, #007664 0%, #005a4a 100%);
      color: white;
      padding: 1.5rem 2rem;
      margin: -24px -24px 0 -24px;
      border-radius: 4px 4px 0 0;

      .header-content {
        display: flex;
        align-items: center;
        gap: 1rem;

        .header-icon {
          font-size: 2.5rem;
          width: 2.5rem;
          height: 2.5rem;
          color: white;
        }

        .header-title {
          margin: 0;
          font-size: 2rem;
          font-weight: 700;
          letter-spacing: -0.5px;
          line-height: 1.2;
        }
      }

      .close-btn {
        color: white;
        flex-shrink: 0;

        &:hover {
          background-color: rgba(255, 255, 255, 0.1);
        }
      }
    }

    mat-dialog-content {
      padding: 2rem;
      max-height: 65vh;
      overflow-y: auto;
      flex: 1;
    }

    .timezone-banner {
      background-color: #e0f2fe;
      border: 1px solid #7dd3fc;
      border-radius: 0.5rem;
      padding: 1rem;
      display: flex;
      align-items: flex-start;
      gap: 0.75rem;
      margin-bottom: 1.5rem;
      color: #0c4a6e;

      mat-icon {
        color: #0284c7;
        font-size: 1.25rem;
        width: 1.25rem;
        height: 1.25rem;
        flex-shrink: 0;
        margin-top: 2px;
      }

      span {
        font-size: 0.875rem;
        line-height: 1.5;
        font-weight: 500;
      }
    }
.form-error-banner {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #dc2626;
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  margin: 0 2rem 1rem 2rem;
}
    .event-form {
      display: flex;
      flex-direction: column;
      gap: 1.5rem;
    }

    .form-row {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 1.5rem;

      @media (max-width: 700px) {
        grid-template-columns: 1fr;
      }
    }

    .date-time-group {
      display: grid;
      grid-template-columns: 2fr 1fr;
      gap: 1rem;
      align-items: flex-start;

      @media (max-width: 700px) {
        grid-template-columns: 1fr;
      }

      .date-field {
        width: 100%;
      }

      .time-field {
        width: 100%;
      }
    }

    mat-form-field {
      width: 100%;

      &.full-width {
        width: 100%;
      }
    }

    mat-dialog-actions {
      padding: 1.5rem 2rem;
      border-top: 1px solid #e5e7eb;
      gap: 1rem;
      background-color: #f9fafb;

      button {
        min-width: 120px;
      }
    }

    /* Custom scrollbar styling */
    mat-dialog-content::-webkit-scrollbar {
      width: 6px;
    }

    mat-dialog-content::-webkit-scrollbar-track {
      background: #f1f5f9;
    }

    mat-dialog-content::-webkit-scrollbar-thumb {
      background: #cbd5e1;
      border-radius: 3px;

      &:hover {
        background: #94a3b8;
      }
    }
  `]
})
export class EventFormDialogComponent implements OnInit {
  eventForm!: FormGroup;
  timeOptions: string[] = [];
private cloudinaryUploadService = inject(CloudinaryUploadService);

  @ViewChild('imageFileInput') imageFileInput!: ElementRef<HTMLInputElement>;
private authBackendUrl = inject(authBackendEndPointToken);
private dialog = inject(MatDialog)
  isUploadingImage = false;
  imageUploadError = '';
  isSubmitting = false;
dateRangeError = false;
readonly minDate = new Date();
readonly separatorKeysCodes: number[] = [ENTER, COMMA];
externalAttendeeEmails: string[] = [];
externalEmailError = '';

private readonly emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
readonly participantOptions: { code: string; display: string }[] = [
  { code: 'public', display: 'Public (Everyone)' },
  ...AVAILABLE_PRACTITIONER_ROLE_OPTIONS
];
addExternalEmail(event: any): void {
  const value = (event.value || '').trim();
  this.externalEmailError = '';

  if (!value) return;

  if (!this.emailPattern.test(value)) {
    this.externalEmailError = `"${value}" is not a valid email address.`;
    event.chipInput?.clear?.();
    return;
  }

  if (this.externalAttendeeEmails.some(e => e.toLowerCase() === value.toLowerCase())) {
    this.externalEmailError = `${value} has already been added.`;
    event.chipInput?.clear?.();
    return;
  }

  this.externalAttendeeEmails.push(value);
  event.chipInput?.clear?.();
}

removeExternalEmail(email: string): void {
  this.externalAttendeeEmails = this.externalAttendeeEmails.filter(e => e !== email);
}
get isHybridPlatform(): boolean {
  return this.eventForm?.get('platform')?.value === 'Hybrid';
}

get showLocationField(): boolean {
  const platform = this.eventForm?.get('platform')?.value;
  return platform === 'Physical' || platform === 'Hybrid';
}

readonly autoGeneratedPlatforms = ['Zoom', 'Google Meet', 'Microsoft Teams', 'Webex'];

/** Returns the provider that will auto-generate the meeting, or null if none applies */
get autoMeetingProvider(): string | null {
  const platform = this.eventForm?.get('platform')?.value;
  const virtualType = this.eventForm?.get('virtualPlatformType')?.value;

  if (this.autoGeneratedPlatforms.includes(platform)) {
    return platform;
  }
  if (platform === 'Hybrid' && this.autoGeneratedPlatforms.includes(virtualType)) {
    return virtualType;
  }
  return null;
}

get isAutoGeneratedMeeting(): boolean {
  return this.autoMeetingProvider !== null;
}
private authService = inject(AuthService);

get isSuperAdmin(): boolean {
  return !!this.authService.currentUserValue?.isSuperAdmin;
}



  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<EventFormDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: FormDialogData
  ) {
    this.generateTimeOptions();
  }
private http = inject(HttpClient);

organizations: { id: string; name: string }[] = []
  ngOnInit(): void {
  this.initForm();
if (this.isSuperAdmin) {
    this.organizations = this.data.organizations ?? [];
  }
  this.eventForm.get('platform')?.valueChanges.subscribe(() => {
    this.updateConditionalValidators();
    this.updateMeetingUrlState();
  });

  this.eventForm.get('virtualPlatformType')?.valueChanges.subscribe(() => {
    this.updateMeetingUrlState();
  });
this.eventForm.get('startDate')?.valueChanges.subscribe(() => { this.dateRangeError = false; this.durationError = false; });
this.eventForm.get('startTime')?.valueChanges.subscribe(() => { this.dateRangeError = false; this.durationError = false; });
this.eventForm.get('endDate')?.valueChanges.subscribe(() => { this.dateRangeError = false; this.durationError = false; });
this.eventForm.get('endTime')?.valueChanges.subscribe(() => { this.dateRangeError = false; this.durationError = false; });
this.eventForm.get('platform')?.valueChanges.subscribe(() => { this.durationError = false; }); // provider changes → recheck
this.eventForm.get('startDate')?.valueChanges.subscribe(() => { this.dateRangeError = false; this.durationError = false; this.pastDateError = false; });
this.eventForm.get('virtualPlatformType')?.valueChanges.subscribe(() => { this.durationError = false; });
  // Apply correct state on load (e.g. editing an existing event)
  this.updateConditionalValidators();
  this.updateMeetingUrlState();


}
private loadOrganizations(): void {
  this.http.get<{ id: string; name: string }[]>(`${this.authBackendUrl}/authBackend/api/organization/lightweight`)
    .subscribe({
      next: (orgs) => this.organizations = orgs,
      error: (err) => console.error('Failed to load organizations:', err)
    });
}
onTargetOrgsChange(event: { value: string[] }): void {
  const selected = event.value;
  const control = this.eventForm.get('targetOrganizationIds');
  if (!control) return;

  const hasAll = selected.includes('all');

  if (hasAll && selected.length > 1) {
    // "All Organizations" was just picked alongside specific orgs, or vice versa —
    // whichever was picked LAST wins. Since mat-select's event fires with the full
    // new selection, we check what the previous value was to know which was added last.
    const previousValue: string[] = control.value ?? [];
    const allWasAlreadySelected = previousValue.includes('all');

    if (allWasAlreadySelected) {
      // User picked a specific org while "All" was active — drop "All", keep the specific org(s)
      control.setValue(selected.filter(v => v !== 'all'), { emitEvent: false });
    } else {
      // User just picked "All" while specific orgs were selected — drop the specific orgs, keep only "All"
      control.setValue(['all'], { emitEvent: false });
    }
  }
}
private updateConditionalValidators(): void {
  const platform = this.eventForm.get('platform')?.value;
  const locationControl = this.eventForm.get('location');
  const virtualTypeControl = this.eventForm.get('virtualPlatformType');

  const needsLocation = platform === 'Physical' || platform === 'Hybrid';
  const needsVirtualType = platform === 'Hybrid';

  locationControl?.setValidators(needsLocation ? [Validators.required] : []);
  locationControl?.updateValueAndValidity({ emitEvent: false });

  virtualTypeControl?.setValidators(needsVirtualType ? [Validators.required] : []);
  virtualTypeControl?.updateValueAndValidity({ emitEvent: false });

  // Note: we don't clear the control's *value* here on purpose — if the admin
  // toggles platform back and forth, we don't want to wipe out what they typed.
  // Irrelevant values get nulled out at submit time in onSubmit() instead.
}

private updateMeetingUrlState(): void {
  const meetingUrlControl = this.eventForm.get('meetingUrl');
  if (!meetingUrlControl) return;

  const urlPattern = /^(https?:\/\/.+)?$/;

  if (this.isAutoGeneratedMeeting) {
    meetingUrlControl.disable({ emitEvent: false });
    meetingUrlControl.setValidators([Validators.pattern(urlPattern)]);
  } else {
    meetingUrlControl.enable({ emitEvent: false });
    meetingUrlControl.setValidators([Validators.pattern(urlPattern)]);
  }
  meetingUrlControl.updateValueAndValidity({ emitEvent: false });
}

  generateTimeOptions(): void {
    this.timeOptions = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const h = String(hour).padStart(2, '0');
        const m = String(minute).padStart(2, '0');
        this.timeOptions.push(`${h}:${m}`);
      }
    }
  }

  /**
   * Get user's timezone abbreviation for display
   */
  getUserTimezone(): string {
    try {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZoneName: 'short'
      });
      const parts = formatter.formatToParts(new Date());
      const timeZonePart = parts.find(part => part.type === 'timeZoneName');
      return timeZonePart?.value || 'Local Time';
    } catch (error) {
      console.error('Error getting timezone:', error);
      return 'Local Time';
    }
  }

  /**
   * CRITICAL FIX: Convert SQL Server UTC datetime to local Date object
   * SQL Server format: "2026-01-28 16:00:00.0000000" (UTC)
   * Need to convert to: Date object in user's local timezone
   */
  convertUTCToLocalDate(utcDateTimeString: string): Date {
    if (!utcDateTimeString) {
      return new Date();
    }

    // SQL Server datetime format: "2026-01-28 16:00:00.0000000"
    // Convert to ISO format with Z suffix to indicate UTC
    // "2026-01-28 16:00:00" → "2026-01-28T16:00:00Z"
    const isoString = utcDateTimeString.replace(' ', 'T').split('.')[0] + 'Z';

    // Create Date object - browser automatically converts UTC to local timezone
    // Database has: 16:00 UTC
    // User in Nigeria sees: 17:00 WAT (UTC+1)
    const localDate = new Date(isoString);

    console.log('UTC from DB:', utcDateTimeString);
    console.log('ISO String:', isoString);
    console.log('Local Date:', localDate.toString());

    return localDate;
  }
// Map each provider to its realistic max single-occurrence duration (hours)
readonly providerMaxDurationHours: Record<string, number> = {
  'Webex': 24,
  'Zoom': 24,          // adjust if you're on a free/basic Zoom plan — could be as low as 40 min
  'Google Meet': 24,
  'Microsoft Teams': 24,
};

durationError = false; // add near your other error flags

private getOccurrenceDurationHours(): number {
  const formValue = this.eventForm.getRawValue();
  const start = new Date(formValue.startDate);
  const [sh, sm] = formValue.startTime.split(':');
  start.setHours(+sh, +sm, 0, 0);

  const end = new Date(formValue.endDate);
  const [eh, em] = formValue.endTime.split(':');
  end.setHours(+eh, +em, 0, 0);

  return (end.getTime() - start.getTime()) / (1000 * 60 * 60);
}

private validateMeetingDuration(): boolean {
  const provider = this.autoMeetingProvider;
  if (!provider) return true; // Physical events, no auto-generated meeting — no constraint

  const maxHours = this.providerMaxDurationHours[provider];
  if (!maxHours) return true; // unknown provider, don't block

  return this.getOccurrenceDurationHours() <= maxHours;
}
  initForm(): void {
    const urlPattern = /^(https?:\/\/.+)?$/;

    // When editing, convert UTC times from backend to local time for display
    let startDate = null;
    let startTime = '';
    let endDate = null;
    let endTime = '';

    if (this.data.event?.start) {
      // CRITICAL: Properly convert UTC to local time
      // Database: "2026-01-28 16:00:00" (UTC)
      // Form shows: 17:00 for Nigeria user (UTC+1)
      const startDateTime = this.convertUTCToLocalDate(this.data.event.start);
      startDate = startDateTime;
      startTime = this.formatTimeForInput(startDateTime);
    }

    if (this.data.event?.end) {
      const endDateTime = this.convertUTCToLocalDate(this.data.event.end);
      endDate = endDateTime;
      endTime = this.formatTimeForInput(endDateTime);
    }
this.externalAttendeeEmails = this.data.event?.externalAttendeeEmails ?? [];

 this.eventForm = this.fb.group({
  title: [this.data.event?.title || '', Validators.required],
  description: [this.data.event?.description || ''],
  startDate: [startDate, Validators.required],
  startTime: [startTime, Validators.required],
  endDate: [endDate, Validators.required],
  endTime: [endTime, Validators.required],
  type: [this.data.event?.type || '', Validators.required],
  platform: [this.data.event?.platform || '', Validators.required],
  virtualPlatformType: [this.data.event?.virtualPlatformType || ''],
  location: [this.data.event?.location || ''],
  participants: [this.data.event?.participants || []],
  maxAttendees: [this.data.event?.maxAttendees || ''],
  tags: [this.data.event?.tags ? (this.data.event.tags as string[]).join(', ') : ''],
  meetingUrl: [this.data.event?.meetingUrl || '', Validators.pattern(urlPattern)],
  registrationUrl: [this.data.event?.registrationUrl || '', Validators.pattern(urlPattern)], // ✅ new


  image: [this.data.event?.image || ''],
  isRecurring: [this.data.event?.isRecurring || false],
recurrenceFrequency: [this.data.event?.recurrenceFrequency || 'DAILY'],
recurrenceCount: [this.data.event?.recurrenceCount || 2],
 targetOrganizationIds: [
  (this.data.event?.targetOrganizationIds?.length ?? 0) > 0
    ? this.data.event!.targetOrganizationIds
    : ['all']
],


});
  }

  /**
   * Format time from Date object to HH:mm string
   */
  formatTimeForInput(date: Date): string {
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  triggerImageUpload(): void {
    this.imageFileInput.nativeElement.value = '';
    this.imageFileInput.nativeElement.click();
  }

onImageFileSelected(event: globalThis.Event): void {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  if (!file) return;

  if (!file.type.startsWith('image/')) {
    this.imageUploadError = 'Please select an image file.';
    return;
  }

  const maxSize = 10 * 1024 * 1024; // raw upload cap before cropping — final cropped file will be much smaller
  if (file.size > maxSize) {
    this.imageUploadError = 'Image must be smaller than 10MB.';
    return;
  }

  this.imageUploadError = '';

  // Open the crop dialog with the raw selected file — nothing is uploaded yet
  const cropRef = this.dialog.open(ImageCropDialogComponent, {
    width: '90vw',
    maxWidth: '750px',
    data: { imageFile: file }
  });

  cropRef.afterClosed().subscribe((result) => {
    if (result?.success && result.file) {
      this.uploadCroppedImage(result.file);
    }
  });
}

private uploadCroppedImage(croppedFile: File): void {
  this.isUploadingImage = true;

  this.cloudinaryUploadService.uploadFile(croppedFile).subscribe({
    next: (result) => {
      this.eventForm.patchValue({ image: result.secure_url });
      this.isUploadingImage = false;
    },
    error: (err) => {
      console.error('Image upload failed:', err);
      this.imageUploadError = 'Failed to upload image. Please try again.';
      this.isUploadingImage = false;
    }
  });
}

  removeImage(): void {
    this.eventForm.patchValue({ image: '' });
  }
pastDateError = false;

private validateDateRange(): { valid: boolean; reason?: 'past' | 'order' } {
  const formValue = this.eventForm.getRawValue();
  if (!formValue.startDate || !formValue.startTime || !formValue.endDate || !formValue.endTime) {
    return { valid: true };
  }

  const start = new Date(formValue.startDate);
  const [sh, sm] = formValue.startTime.split(':');
  start.setHours(+sh, +sm, 0, 0);

  const end = new Date(formValue.endDate);
  const [eh, em] = formValue.endTime.split(':');
  end.setHours(+eh, +em, 0, 0);

  if (start < new Date()) {
    return { valid: false, reason: 'past' };
  }
  if (end <= start) {
    return { valid: false, reason: 'order' };
  }
  return { valid: true };
}

  /**
   * CRITICAL: This method handles timezone conversion properly
   * User enters times in their LOCAL timezone
   * We convert to UTC before sending to backend
   * Backend stores as UTC
   * When displaying, we convert UTC back to local time
   */
onSubmit(): void {
   if (this.eventForm.invalid || this.isSubmitting) return;

    this.dateRangeError = false;


this.pastDateError = false;

const dateCheck = this.validateDateRange();
if (!dateCheck.valid) {
  if (dateCheck.reason === 'past') {
    this.pastDateError = true;
  } else {
    this.dateRangeError = true;
  }
  return;
}
  if (!this.validateMeetingDuration()) {
  this.durationError = true;
  return;
}
  this.isSubmitting = true;
    const formValue = this.eventForm.getRawValue();

    const startDate = new Date(formValue.startDate);
    const [startHours, startMinutes] = formValue.startTime.split(':');
    startDate.setHours(parseInt(startHours), parseInt(startMinutes), 0, 0);

    const endDate = new Date(formValue.endDate);
    const [endHours, endMinutes] = formValue.endTime.split(':');
    endDate.setHours(parseInt(endHours), parseInt(endMinutes), 0, 0);
const selectedOrgIds: string[] = formValue.targetOrganizationIds ?? [];
const isAllOrgs = selectedOrgIds.includes('all') || selectedOrgIds.length === 0;
    const eventData = {
  title: formValue.title,
  description: formValue.description,
  type: formValue.type,
  platform: formValue.platform,
  virtualPlatformType: formValue.platform === 'Hybrid' ? formValue.virtualPlatformType : null,
  location: this.showLocationField ? formValue.location : null,
  participants: formValue.participants,
  maxAttendees: formValue.maxAttendees || null,
  tags: formValue.tags
    ? formValue.tags.split(',').map((tag: string) => tag.trim()).filter((tag: string) => tag)
    : [],
  meetingUrl: this.isAutoGeneratedMeeting ? null : (formValue.meetingUrl || null),
  registrationUrl: formValue.registrationUrl || null,
   externalAttendeeEmails: this.externalAttendeeEmails,
  image: formValue.image || null,
  start: startDate.toISOString(),
  end: endDate.toISOString(),
  organizationId: this.data.organizationId,
   targetOrganizationIds: this.isSuperAdmin && !isAllOrgs
    ? selectedOrgIds
    : null,

};

    this.dialogRef.close({ success: true, data: eventData });
  }

  close(): void {
  this.dialogRef.close({ success: false });
}
}

